import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Group, Modal, Spinner } from '@economic/gaudi';

import { Form, Field } from '../../../../../shared/components/Form';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import LoadingBox from '../../../../home/PlanUpgradeDialog/LoadingBox';
import { redirectToLegacyPage } from '@economic/react-app';

function CopyInvoice() {
    const navigate = useNavigate();
    const location = useLocation();
    const [fetching, setFetching] = useState(true);
    const [hasNegativeInventoryAmountsError, setHasNegativeInventoryAmountsError] = useState(false);
    const { draftInvoiceNumber, invoiceNumber, returnType, customerBarred } = location.state || ({} as any);

    const loadingTexts = useLocalization(textIds);
    const [showUpdateMasterInformation, setShowUpdateMasterInformation] = useState(false);

    useEffect(() => {
        async function fetch() {
            if (draftInvoiceNumber) {
                const copyInfo = await internalApiClient.get(`/salesDocument/${draftInvoiceNumber}/copy-info`);
                setShowUpdateMasterInformation(copyInfo.data.customerInformationOutdated);
            } else {
                const copyInfo = await internalApiClient.get(`/salesDocument/booked/${invoiceNumber}/copy-info`);
                setShowUpdateMasterInformation(copyInfo.data.customerInformationOutdated);
            }
        }

        fetch().finally(() => setFetching(false));
    }, [draftInvoiceNumber, invoiceNumber]);

    if (!draftInvoiceNumber && !invoiceNumber) {
        return null;
    }

    const disclosure = <RouteDisclosure routeTrigger={'#copy-invoice'} />;

    function hide() {
        navigate(location.pathname);
    }

    async function handleSubmit(values) {
        const { status, updateMasterInformation } = values;

        if (status === 2) {
            let hasNegativeAmounts;
            if (draftInvoiceNumber) {
                hasNegativeAmounts = (
                    await internalApiClient.get<boolean>(
                        `/inventory/invoices/draft/${draftInvoiceNumber}/hasNegativeInventoryAmounts`
                    )
                ).data;
            } else {
                hasNegativeAmounts = (
                    await internalApiClient.get<boolean>(
                        `/inventory/invoices/booked/${invoiceNumber}/hasNegativeInventoryAmounts`
                    )
                ).data;
            }

            if (hasNegativeAmounts) {
                setHasNegativeInventoryAmountsError(true);
                return;
            }
        }

        if (draftInvoiceNumber) {
            redirectToLegacyPage(
                `${process.env.ECONOMIC_PATH}/include/DataForms/KopierFaktura.asp?kladdenr=${draftInvoiceNumber}&returnertil=102&form=101&cp=yes&nystatus=${status}&updateMasterInformation=${updateMasterInformation}`
            );
        } else {
            redirectToLegacyPage(
                `${process.env.ECONOMIC_PATH}/include/DataForms/KopierBogfoertFaktura.asp?fakturanr=${invoiceNumber}&bogf=0&status=${status}&returfaktype=${returnType}&returnertil=104&updateMasterInformation=${updateMasterInformation}`
            );
        }

        hide();
    }

    if (loadingTexts) {
        return (
            <Modal aria-label={t`TOAST_LOADING`} disclosure={disclosure} onHide={hide}>
                <Spinner />
            </Modal>
        );
    }

    const initialData = {
        status: 3,
        draftInvoiceNumber,
        invoiceNumber,
        returnType,
        updateMasterInformation: false,
    };

    if (fetching) {
        return (
            <Modal aria-label={t`COPYBOOKEDINVOICE_HEADLINE`} disclosure={disclosure} onHide={hide} size="xs">
                <LoadingBox />
            </Modal>
        );
    }

    return (
        <Modal aria-label={t`COPYBOOKEDINVOICE_HEADLINE`} disclosure={disclosure} onHide={hide} size="xs">
            <Form initialData={initialData} onSubmit={handleSubmit} skipNotifications>
                <Modal.Header>{t`COPYBOOKEDINVOICE_HEADLINE`}</Modal.Header>
                <Modal.Content style={{ marginLeft: '16px', marginBottom: '0' }}>
                    <Field.NumberRadioGroup
                        name="status"
                        options={[
                            {
                                text: t`COPYBOOKEDINVOICE_TYPE_OFFER`,
                                value: 1,
                                disabled: customerBarred,
                            },
                            {
                                text: t`COPYBOOKEDINVOICE_TYPE_ORDER`,
                                value: 2,
                                disabled: customerBarred,
                            },
                            {
                                text: t`COPYBOOKEDINVOICE_TYPE_INVOICE`,
                                value: 3,
                            },
                        ]}
                    />
                    {hasNegativeInventoryAmountsError && (
                        <p style={{ color: 'red' }}>{t`FORM_COPY_INVOICE_NEG_STOCK_QUANTITY_ERROR_TEXT`}</p>
                    )}
                    {showUpdateMasterInformation && (
                        <>
                            <hr />
                            <Field.Checkbox name="updateMasterInformation" text={t`FORM_COPY_INVOICE_UPDATEMASTERINFORMATION`} />
                        </>
                    )}
                </Modal.Content>
                <Modal.Footer className="mt-4">
                    <Group>
                        <Button appearance="default" onClick={hide}>{t`COPYBOOKEDINVOICE_CANCEL`}</Button>
                        <Button appearance="primary" autoFocus type="submit" className="ml-4">{t`COPYBOOKEDINVOICE_OK`}</Button>
                    </Group>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

const textIds = [
    'COPYBOOKEDINVOICE_HEADLINE',
    'COPYBOOKEDINVOICE_STATUS',
    'COPYBOOKEDINVOICE_TYPE_OFFER',
    'COPYBOOKEDINVOICE_TYPE_ORDER',
    'COPYBOOKEDINVOICE_TYPE_INVOICE',
    'COPYBOOKEDINVOICE_OK',
    'COPYBOOKEDINVOICE_CANCEL',
    'FORM_COPY_INVOICE_NEG_STOCK_QUANTITY_ERROR_TEXT',
    'FORM_COPY_INVOICE_UPDATEMASTERINFORMATION',
];

export default CopyInvoice;
